import { useAtom } from 'jotai'
import * as React from 'react'
import styled from 'styled-components'
import InfiniteScroll from 'react-infinite-scroller'
import { Spinner } from '@geist-ui/react'

import { BaseFollowButton } from '..'
import { FOLLOW_DIALOG_ACTIVE_TAB, followDialogActiveTabAtom } from '../../../atoms/profile'
import { rwStreamClientAtom } from '../../../atoms/StreamClient'

import { fbEnrichUsersFromIds } from '../../../data/FirebaseEnrich'
import { useInfiniteLoadByOffset } from '../../../hooks/infiniteLoadByOffset'
import { FbEnrichedUser } from '../../../interfaces/User'
import { useMediaQuery } from '@material-ui/core'
import { useRouter } from 'next/router'
import ProfileImage from '../image'
import { SmallSpinner, SmallSpinnerContainer } from '../../spinner'
import useAuth from '../../../hooks/auth'
import { captureException } from '@sentry/nextjs'

// ========================== React component ==================================

const PAGE_SIZE = 10

const RowContainer = styled.div``

const Row = styled.div`
  display: flex;
  align-items: center;
`

const Name = styled.div`
  cursor: pointer;
  color: #ffffff;
  font-size: 14px;
  font-weight: 700;
  margin-left: 15px;
  &:hover {
    text-decoration: underline;
  }

  @media (min-width: 43em) {
    font-size: 20px;
  }
`

const Spacer = styled.div`
  flex: 1;
`

const Center = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
`

const LargeSpinner = styled(Spinner)`
  min-width: 96px;
  min-height: 96px;
`

const NoItems = styled.div`
  color: #aaa;
  font-weight: 600;
  font-size: 18px;
`

const Divider = styled.div`
  height: 1px;
  background-color: #323232;
  width: 100%;
  margin: 15px 0;
`

type BooleanMap = { [key: string]: boolean }

const getAllTrueMap = (userIds: string[]): BooleanMap =>
  userIds.reduce((map: BooleanMap, userId: string) => ({ ...map, [userId]: true }), {})

interface Props {
  isForFollow?: boolean
  address: string
  setFollowingCount?: Function
  followingCount?: number
  currentIndex: number
  index: number
}

const FollowTabContent = ({
  address,
  isForFollow,
  followingCount,
  setFollowingCount,
  currentIndex,
  index,
}: Props): JSX.Element => {
  const [streamClient] = useAtom(rwStreamClientAtom)
  const { profile } = useAuth()
  const notificationFeed = streamClient.feed('notifications', address)
  const videoFeed = streamClient.feed('videos', address)
  const myNotificationFeed = streamClient.feed('notifications', profile?.address)
  const fullscreen = useMediaQuery('(max-width: 43em)')
  const isMyProfile = profile?.address === address
  const [_, setFollowDialogActiveTab] = useAtom(followDialogActiveTabAtom)
  const router = useRouter()

  const idPrefix = isForFollow ? 'notifications:' : 'videos:'
  const fieldName = isForFollow ? 'feed_id' : 'target_id'

  const getIdFromMyFollowingResponseItem = ({ target_id }: { target_id: string }): string =>
    target_id.replace('videos:', '')
  const [mapForIsUpdating, setMapForIsUpdating] = React.useState<BooleanMap>({})
  const [mapForIsFollowing, setMapForIsFollowing] = React.useState<BooleanMap>({})

  const getFollowing = React.useCallback(
    (params: any) => notificationFeed.following(params),
    [notificationFeed]
  )
  const getFollowers = React.useCallback((params: any) => videoFeed.followers(params), [videoFeed])
  const getMyFollowing = React.useCallback(
    (params: any) => myNotificationFeed.following(params),
    [myNotificationFeed]
  )
  const getItems = isForFollow ? getFollowers : getFollowing
  const getIdFromResponseItem = React.useCallback(
    ({ [fieldName]: id }: { [key: string]: string }): string => id.replace(idPrefix, ''),
    [fieldName, idPrefix]
  )

  const fetchUsers = React.useCallback(
    async ({
      offset,
      limit,
    }: {
      offset: number
      limit: number
    }): Promise<{ rawData: string[]; data: FbEnrichedUser[]; hasMore: boolean }> => {
      const response = await getItems({ offset, limit })
      const userIds = response.results.map(getIdFromResponseItem)
      const enrichedItems = await fbEnrichUsersFromIds(userIds)
      return { rawData: userIds, data: enrichedItems, hasMore: userIds.length === limit }
    },
    [getIdFromResponseItem, getItems]
  )

  const handleNextItems = (items: FbEnrichedUser[]): void => {
    const userIds = items.map(({ address }) => address)

    ;(async () => {
      try {
        setMapForIsUpdating(getAllTrueMap(userIds))
        const followersResponse = await getMyFollowing({
          offset: 0,
          limit: PAGE_SIZE,
          filter: userIds.map((userId) => `videos:${userId}`),
        })
        const returnedIds = followersResponse.results.map(getIdFromMyFollowingResponseItem)
        setMapForIsFollowing((map) => ({
          ...map,
          ...getAllTrueMap(returnedIds),
        }))
      } catch (err) {
        console.error(err)
        captureException(err)
      } finally {
        setMapForIsUpdating({})
      }
    })()
  }

  const { data, isLoading, isRefetching, nextIsLoading, fetchNext, hasNext, refetch } =
    useInfiniteLoadByOffset<FbEnrichedUser, string>({
      id: address,
      getItems: fetchUsers,
      onNextItems: handleNextItems,
      refetchMarker: isForFollow ? 'follow' : 'following',
    })

  React.useEffect(() => {
    if (index === currentIndex) {
      refetch()
    }
  }, [currentIndex, index])

  const renderItem = React.useCallback(
    (item: FbEnrichedUser): any => {
      const { address, displayName } = item
      const handleSetIsFollowing = (value: boolean): void => {
        if (isMyProfile && setFollowingCount) {
          setFollowingCount((followingCount || 0) + (value ? 1 : -1))
        }

        setMapForIsFollowing({
          ...mapForIsFollowing,
          [address]: value,
        })
      }

      const handleSetIsUpdating = (value: boolean): void => {
        setMapForIsUpdating({
          ...mapForIsUpdating,
          [address]: value,
        })
      }

      const handleOpenProfile = () => {
        setFollowDialogActiveTab(FOLLOW_DIALOG_ACTIVE_TAB.NONE)
        router.push(`/${address}`)
      }

      return (
        <RowContainer>
          <Row
            key={`${isForFollow ? 'follow' : 'following'}-item-${address}`}
            onClick={handleOpenProfile}
          >
            <ProfileImage item={item} size={50} />
            <Name>{displayName}</Name>
            <Spacer />
            {profile?.address && (
              <BaseFollowButton
                onUpdate={() => {
                  console.log('')
                }}
                address={address}
                isUpdating={mapForIsUpdating[address]}
                isFollowing={mapForIsFollowing[address]}
                setIsFollowing={handleSetIsFollowing}
                setIsUpdating={handleSetIsUpdating}
              />
            )}
          </Row>
          <Divider />
        </RowContainer>
      )
    },
    [
      followingCount,
      isForFollow,
      isMyProfile,
      mapForIsFollowing,
      mapForIsUpdating,
      profile?.address,
      router,
      setFollowDialogActiveTab,
      setFollowingCount,
    ]
  )

  const handleLoadMore = React.useCallback(() => {
    if (isLoading || nextIsLoading) {
      return
    }

    fetchNext()
  }, [fetchNext, isLoading, nextIsLoading])

  return (
    <div
      style={{
        height: fullscreen ? 'calc(100vh - 188px)' : 'calc(85vh - 188px)',
        overflow: 'auto',
        marginTop: 6,
        paddingRight: 8,
      }}
    >
      {isLoading || isRefetching ? (
        <Center>
          <LargeSpinner />
        </Center>
      ) : !hasNext && !data.length ? (
        <Center>
          <NoItems>{isForFollow ? 'No followers yet.' : 'Not following anyone yet.'}</NoItems>
        </Center>
      ) : (
        <InfiniteScroll
          style={{ marginTop: '15px' }}
          loadMore={handleLoadMore}
          hasMore={hasNext}
          loader={
            <SmallSpinnerContainer>
              <SmallSpinner />
            </SmallSpinnerContainer>
          }
          useWindow={false}
        >
          {data.map(renderItem)}
        </InfiniteScroll>
      )}
    </div>
  )
}

export default FollowTabContent
